import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardBody } from "../../components/Card/CardBody";
import { Card } from "../../components/Card/Card";
import { RightsIntersectionRecord, RightsIntersectionRecordHard, useRightsApi } from "../../api/useRightsApi";
import { Preloader } from "../../components/Preloader";
import { useApiExecutor } from "../../hooks/useApiExecutor";
import {
  IntersectionSearchType,
  RightsIntersectionsFilters,
  RightsIntersectionsFiltersParams
} from "./RightsIntersectionsFilters";
import { MemoizedTable } from "../../components/Table/Table";
import {
  rightsIntersectionsColumnsDefinition,
  rightsIntersectionsColumnsDefinitionShort
} from "./RightsIntersectionsColumnsDefinition";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { ListResponse } from "../../infrastructure/useFetch";

export const RightsIntersectionsPage = () => {
  const { t } = useTranslation();
  const {
    getRightsIntersections,
    postDownloadReportIntersections,
    getGeneratingReportTaskStatus,
    downloadGeneratedReport
  } = useRightsApi();

  const [isLoading, setIsLoading] = useState(false);
  const [searchType] = useQueryParam("search_type", withDefault(StringParam, IntersectionSearchType.FILE));
  const [isFullView, setIsFullView] = useState<boolean>(true);

  const { info, contents, fetch } = useApiExecutor<
    RightsIntersectionRecord,
    RightsIntersectionsFiltersParams
  >({
    showInPage: 100,
    isInitLoadingState: false,
    fetchCallback: getRightsIntersections as any,
    hookAfterFetch: (params, response) => {
      if (params?.date_from == null) {
        setIsFullView(false);
        let preparedResponse = (response as unknown as RightsIntersectionRecordHard);
        return {
          previous: null,
          next: null,
          count: 100,
          results: Object.entries(preparedResponse)
        } as ListResponse<any>;
      }

      setIsFullView(true);
      return response;
    }
  });

  const [generatedReportId, setGeneratedReportId] = useState<string | null>(null);
  const startDownloadReport = useCallback(async (query: RightsIntersectionsFiltersParams) => {
    setIsLoading(true);
    try {
      const data = await postDownloadReportIntersections(query);
      setGeneratedReportId(data.data);
    } catch {
      setIsLoading(false);
      toast.error(t("rights_intersections.download_report_error"));
    }
  }, []);

  const columnsDefinitions = useMemo(() => {
    if (!isFullView) {
      return rightsIntersectionsColumnsDefinitionShort(t) as any;
    }

    return rightsIntersectionsColumnsDefinition(t) as any;
  }, [isFullView]);

  const handleCheckGenerateReportStatus = useCallback(async () => {
    try {
      const { data } = await getGeneratingReportTaskStatus(generatedReportId as string);
      if (data !== "In progress") {
        const file = await downloadGeneratedReport(data as string);
        const regExpFilename = /filename="(?<filename>.*)"/;
        const filename = regExpFilename.exec(file.disposition)?.groups?.filename ?? "CrossWorksReport";
        saveAs(file.data, filename);
        setGeneratedReportId(null);
        setIsLoading(false);
        toast.success(t("rights_intersections.download_report_success"));
      }
    } catch {
      toast.error(t("rights_intersections.download_report_error"));
      setGeneratedReportId(null);
      setIsLoading(false);
    }
  }, [generatedReportId]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (generatedReportId !== null) {
      interval = setInterval(() => {
        void handleCheckGenerateReportStatus();
      }, 5000);
    }

    return () => {
      if (interval != null) {
        clearInterval(interval);
      }
    };
  }, [generatedReportId]);

  return (
    <>
      <div className={"w-full border-solid py-3 xl:px-8 sm:px-4 border-2 border-gray-200 rounded-r rounded-b"}>
        <div className={"mb-3"} />
        <div className={"flex justify-between items-center flex-wrap gap-2"}>
          <RightsIntersectionsFilters
            isLoading={isLoading}
            onApply={searchType === IntersectionSearchType.FILE
              ? startDownloadReport
              : fetch
            }
          />
        </div>
        <div className={"flex justify-between items-center flex-wrap mb-3 gap-2"}>
          {
            (searchType === IntersectionSearchType.RELEASE_ID || searchType === IntersectionSearchType.CODE_ISRC) &&
            <Card className={"w-full"}>
              <CardBody>
                {info?.isPageLoading || info?.isInitialLoading ? (
                  <Preloader size={"3x"} className={"text-muted py-[28px]"} />
                ) : (<MemoizedTable data={contents} columnsDefinition={columnsDefinitions} />
                )}
              </CardBody>
            </Card>
          }

          {
            isLoading &&
            <Card className={"w-full mt-3"}>
              <CardBody>
                <Preloader
                  size={"2x"}
                  message={t("rights_intersections.download_report_long_wait_message")}
                  className={"text-gray-500"}
                />
              </CardBody>
            </Card>
          }
        </div>
      </div>
    </>
  );
};
